import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, darken, styled } from '@mui/material/styles';
import {
  AddCircleOutline as AddCircleOutlineIcon,
  Assignment as AssignmentIcon,
  ContentCopy as ContentCopyIcon,
  DeleteOutline as DeleteOutlineIcon,
  Domain as DomainIcon,
  EmojiEvents as EmojiEventsIcon,
  ManageSearch as ManageSearchIcon,
  Public as PublicIcon,
  TravelExplore as TravelExploreIcon,
} from '@mui/icons-material';
import { BASEURL, PRIMARYCOLOR } from '../../Constants';
import { notifyCreditsChanged } from '../../services/creditsApi';

const HeaderCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 1100,
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3),
  borderRadius: 16,
  background: `linear-gradient(135deg, ${PRIMARYCOLOR} 0%, ${darken(PRIMARYCOLOR, 0.5)} 100%)`,
  color: '#fff',
  boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
}));

const SectionCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 1100,
  borderRadius: 16,
  boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
}));

const StepIconContainer = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: ownerState.active
    ? theme.palette.primary.main
    : ownerState.completed
      ? theme.palette.success.main
      : theme.palette.grey[300],
  color: '#fff',
  width: 36,
  height: 36,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: ownerState.active ? '0 0 0 6px rgba(25,118,210,0.15)' : 'none',
}));

function CustomStepIcon(props) {
  const { active, completed, icon } = props;
  const mapping = {
    1: <AssignmentIcon fontSize="small" />,
    2: <ManageSearchIcon fontSize="small" />,
    3: <EmojiEventsIcon fontSize="small" />,
  };
  return (
    <StepIconContainer ownerState={{ active, completed }}>
      {mapping[icon] || <AssignmentIcon fontSize="small" />}
    </StepIconContainer>
  );
}

const steps = ['Campaign Details', 'Add Target', 'Done'];
const newId = () => (crypto?.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));

const prependNone = (list = []) => {
  const cleaned = (list || []).filter((org) => org && Number(org.id) !== -1);
  return [{ id: -1, name: 'None' }, ...cleaned];
};

function normalizeTarget(value) {
  if (!value) return '';
  const raw = value.trim();
  if (!raw) return '';

  if (/^https?:\/\//i.test(raw)) {
    try {
      const url = new URL(raw);
      const pathname = url.pathname && url.pathname !== '/' ? url.pathname.replace(/\/+$/, '') : '';
      return `${url.protocol}//${url.hostname.toLowerCase()}${url.port ? `:${url.port}` : ''}${pathname}${url.search}`.replace(/\/+$/, '');
    } catch {
      return raw;
    }
  }

  return raw
    .toLowerCase()
    .replace(/^www\./, '')
    .split('/')[0]
    .replace(/:\d+$/, '');
}

function isValidTarget(value) {
  if (!value) return false;

  if (/^https?:\/\//i.test(value)) {
    try {
      const url = new URL(value);
      return /^(?!-)(?:[a-z0-9-]{1,63}\.)+[a-z]{2,}$/i.test(url.hostname);
    } catch {
      return false;
    }
  }

  return /^(?!-)(?:[a-z0-9-]{1,63}\.)+[a-z]{2,}$/i.test(value);
}

const CreateOSINTPentest = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [scanName, setScanName] = useState('');
  const [organizationId, setOrganizationId] = useState(-1);
  const [organizations, setOrganizations] = useState([{ id: -1, name: 'None' }]);
  const [targetInput, setTargetInput] = useState('');
  const [targetError, setTargetError] = useState('');
  const [target, setTarget] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [pentestId, setPentestId] = useState('');
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    fetch(`${BASEURL}/organizations?basic=1`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data?.error) {
          setOrganizations([{ id: -1, name: 'None' }]);
          setOrganizationId(-1);
          return;
        }
        const list = Array.isArray(data?.organizations) ? data.organizations : [];
        setOrganizations(prependNone(list));
        setOrganizationId(-1);
      })
      .catch((error) => {
        console.error('Failed to fetch organizations (basic):', error);
        setOrganizations([{ id: -1, name: 'None' }]);
        setOrganizationId(-1);
      });
  }, [navigate]);

  const canAdd = useMemo(() => targetInput.trim().length > 0 && !target, [targetInput, target]);

  const addTarget = () => {
    const normalized = normalizeTarget(targetInput);
    if (!normalized) {
      setTargetError('Enter a domain or URL, such as example.com or https://example.com.');
      return;
    }
    if (!isValidTarget(normalized)) {
      setTargetError('That does not look like a valid domain or URL.');
      return;
    }
    if (target) {
      setTargetError('OSINT scans support one target for now. Remove the current target to add another.');
      return;
    }

    setTarget({ id: newId(), value: normalized });
    setTargetInput('');
    setTargetError('');
    setApiError('');
  };

  const copyValue = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Clipboard failures are non-blocking.
    }
  };

  const handleSubmit = () => {
    if (!scanName.trim()) {
      setApiError('Add a scan name before creating the OSINT scan.');
      setActiveStep(0);
      return;
    }

    if (!target?.value) {
      setTargetError('Add one domain or URL before creating the OSINT scan.');
      return;
    }

    const token = sessionStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    const payload = {
      name: scanName.trim(),
      scope: target.value,
    };

    if (Number(organizationId) !== -1) {
      payload.organizationId = Number(organizationId);
    }

    setIsSaving(true);
    setApiError('');

    fetch(`${BASEURL}/pentests/osint`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((data) => {
        setIsSaving(false);
        if (data?.success === 1 && data?.pentestId) {
          notifyCreditsChanged({ reason: 'pentest-created', pentestId: data.pentestId });
          setPentestId(data.pentestId);
          setActiveStep(2);
          return;
        }
        setApiError(data?.message || 'Error creating OSINT scan.');
      })
      .catch((error) => {
        console.error('Error creating OSINT scan:', error);
        setIsSaving(false);
        setApiError('Error creating OSINT scan.');
      });
  };

  const resetForm = () => {
    setActiveStep(0);
    setScanName('');
    setOrganizationId(-1);
    setTargetInput('');
    setTargetError('');
    setTarget(null);
    setPentestId('');
    setApiError('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: { xs: 2, md: 4 } }}>
      <HeaderCard>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
              Create OSINT Scan
            </Typography>
            <Typography sx={{ opacity: 0.9 }}>
              Schedule an OSINT agent to collect public exposure signals for a single domain or URL.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Chip
              icon={<TravelExploreIcon />}
              label="OSINT Agent"
              variant="outlined"
              sx={{ mr: 1, borderColor: alpha('#fff', 0.6), color: 'white', '& .MuiChip-icon': { color: '#fff' } }}
            />
            <Chip
              icon={<PublicIcon />}
              label="Public Exposure"
              variant="outlined"
              sx={{ borderColor: alpha('#fff', 0.6), color: 'white', '& .MuiChip-icon': { color: '#fff' } }}
            />
          </Grid>
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel StepIconComponent={CustomStepIcon}>
                    <Typography sx={{ color: '#fff' }}>{label}</Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Grid>
        </Grid>
      </HeaderCard>

      {activeStep === 0 && (
        <SectionCard sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                Campaign Details
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Give the OSINT scan a clear name and optionally attach it to an organization.
              </Typography>
              <TextField
                fullWidth
                label="Scan Name"
                placeholder="e.g., Acme OSINT Scan"
                value={scanName}
                onChange={(event) => setScanName(event.target.value)}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth>
                <InputLabel id="osint-org-select-label">Organization</InputLabel>
                <Select
                  labelId="osint-org-select-label"
                  label="Organization"
                  value={organizationId}
                  onChange={(event) => setOrganizationId(Number(event.target.value))}
                >
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button variant="outlined" onClick={() => navigate(-1)}>
                  Back
                </Button>
                <Button variant="contained" onClick={() => setActiveStep(1)} disabled={!scanName.trim()}>
                  Next
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Box
                sx={{
                  width: '100%',
                  minHeight: 220,
                  borderRadius: 3,
                  p: 3,
                  bgcolor: 'rgba(0,0,0,0.04)',
                  border: '1px dashed rgba(0,0,0,0.12)',
                }}
              >
                <Typography fontWeight={800} sx={{ mb: 1 }}>
                  What this scan needs
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  OSINT scans currently support one domain or URL. The agent will use that scope to gather public
                  signals that can help reveal exposed technology, references, and attack surface context.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </SectionCard>
      )}

      {activeStep === 1 && (
        <SectionCard sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Add OSINT Target
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter one domain or URL. You can remove it and add a different target before creating the scan.
          </Typography>

          {apiError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          ) : null}

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                fullWidth
                size="small"
                label="Domain or URL"
                placeholder="example.com or https://example.com"
                value={targetInput}
                onChange={(event) => {
                  setTargetInput(event.target.value);
                  setTargetError('');
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && canAdd) addTarget();
                }}
                error={!!targetError}
                helperText={targetError || null}
                FormHelperTextProps={{ sx: { m: targetError ? '3px 14px 0' : 0 } }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DomainIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': { height: 44, alignItems: 'center' },
                  '& .MuiInputBase-input': { py: 0 },
                }}
              />
            </Grid>
            <Grid item xs={12} md="auto" sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                startIcon={<AddCircleOutlineIcon />}
                variant="contained"
                onClick={addTarget}
                disabled={!canAdd}
                sx={{ height: 44 }}
              >
                Add
              </Button>
            </Grid>
          </Grid>

          <Paper elevation={0} sx={{ mt: 3, borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5 }}>
              <Typography fontWeight={800}>Target ({target ? 1 : 0})</Typography>
              <Tooltip title="Remove current target">
                <span>
                  <Button color="inherit" size="small" onClick={() => setTarget(null)} disabled={!target}>
                    Clear
                  </Button>
                </span>
              </Tooltip>
            </Box>
            <Divider />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={140}>Type</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell width={120} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!target ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        No OSINT target added yet. Add a domain or URL above.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow hover>
                    <TableCell>
                      <Chip size="small" icon={<DomainIcon fontSize="small" />} label="Target" sx={{ fontWeight: 600, bgcolor: 'rgba(0,0,0,0.04)' }} />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', wordBreak: 'break-word' }}>
                      {target.value}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Copy">
                        <IconButton size="small" onClick={() => copyValue(target.value)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setTarget(null)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button variant="outlined" onClick={() => setActiveStep(0)}>
              Back
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={!target || isSaving}>
              {isSaving ? <CircularProgress size={24} /> : 'Create OSINT Scan'}
            </Button>
          </Box>
        </SectionCard>
      )}

      {activeStep === 2 && (
        <SectionCard sx={{ p: 4, textAlign: 'center', background: 'linear-gradient(135deg, #E3F2FD 0%, #F7FBFF 100%)' }}>
          <EmojiEventsIcon sx={{ fontSize: 56, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 900, mt: 1 }}>
            OSINT scan created
          </Typography>
          <Typography sx={{ mt: 1, color: 'text.secondary' }}>
            Your OSINT scan has been scheduled. You can find it from the pentests page.
          </Typography>
          {pentestId ? (
            <Chip label={`Pentest ID: ${pentestId}`} sx={{ mt: 2, fontWeight: 800 }} />
          ) : null}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
            <Button variant="outlined" onClick={resetForm}>
              Create Another
            </Button>
            <Button variant="contained" onClick={() => navigate('/pentests')}>
              Finish
            </Button>
          </Box>
        </SectionCard>
      )}
    </Box>
  );
};

export default CreateOSINTPentest;
